import { useState, useEffect } from 'react';
import { Button, Checkbox, Select, NumberInput, TextInput, Text } from '@mantine/core';
import apiService from '../services/api';
import LanguageTabs from './LanguageTabs';
import { localizedField } from '../utils/localizedField';
import './AddEditOfferModal.css';

const getDefaultDateTime = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const RULE_TYPES = [
  { value: 'percentage', label: 'Percentage Discount' },
  { value: 'fixed', label: 'Fixed Amount Discount' },
  { value: 'free_nth_order', label: 'Free Product on Nth Order' },
  { value: 'min_order_discount', label: '% Discount Above Min. Amount' },
  { value: 'free_shipping', label: 'Free Shipping' },
  { value: 'buy_x_pay_y', label: 'Buy X Pay Y' },
];

const RULE_HELP = {
  percentage: 'Apply a percentage discount on matching products.',
  fixed: 'Apply a fixed CHF discount on matching products.',
  free_nth_order: 'Give a specific product free on the user\'s Nth paid order. The SKU you select is the free product.',
  min_order_discount: 'Apply a percentage discount store-wide when the cart subtotal meets the minimum amount.',
  free_shipping: 'Make all delivery orders free shipping during the promo period. No scope needed.',
  buy_x_pay_y: 'Buy X items and get Y free. Applies in the same order. If no trigger product is set, a reward product is required.',
};

export default function AddEditOfferModal({ offer, cloneData, onClose, onSaved }) {
  const source = cloneData || offer;
  const isClone = !!cloneData;
  const parseExistingConfig = (o) => {
    if (!o?.rule_config) return {};
    try { return typeof o.rule_config === 'string' ? JSON.parse(o.rule_config) : o.rule_config; }
    catch { return {}; }
  };

  const existingConfig = source ? parseExistingConfig(source) : {};

  const [form, setForm] = useState(() => ({
    product_sku: source?.product_sku || '',
    category: source?.category || '',
    rule_type: source?.rule_type || 'percentage',
    value: source?.value ?? '',
    start_time: source?.start_time?.slice(0, 16) || getDefaultDateTime(),
    end_time: source?.end_time?.slice(0, 16) || getDefaultDateTime(),
    description: '',
    translations: source?.translations || [],
    active: source?.active ?? true,
    business_only: !!source?.business_only,
    nth_order: existingConfig.nth_order || '',
    min_amount: existingConfig.min_amount || '',
    buy_qty: existingConfig.buy_qty || '',
    pay_qty: existingConfig.pay_qty || '',
    reward_product_sku: existingConfig.reward_variant_id || '',
    trigger_product_sku: existingConfig.trigger_variant_id || '',
  }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    apiService.getProductCategories().then(setCategories).catch(() => {});
    apiService.getProducts({ include_hidden: true }).then(setProducts).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNumberChange = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
  };

  const buildPayload = () => {
    const base = {
      description: localizedField(form, 'description', 'en') || form.description || '',
      translations: form.translations || [],
      start_time: form.start_time,
      end_time: form.end_time,
      active: form.active,
      business_only: form.business_only,
      rule_type: form.rule_type,
    };

    if (form.rule_type === 'percentage' || form.rule_type === 'fixed') {
      if (form.product_sku) {
        const variant = products.flatMap(p => p.variants || []).find(v => v.sku === form.product_sku);
        if (variant) base.variant_id = variant.uuid;
      }
      if (form.category) base.category = form.category;
    }

    if (form.rule_type === 'free_nth_order') {
      if (form.product_sku) {
        const variant = products.flatMap(p => p.variants || []).find(v => v.sku === form.product_sku);
        if (variant) base.variant_id = variant.uuid;
      }
    }

    if (form.rule_type === 'buy_x_pay_y' && form.category) {
      base.category = form.category;
    }

    switch (form.rule_type) {
      case 'percentage':
      case 'min_order_discount':
        base.value = parseFloat(form.value) || 0;
        if (form.rule_type === 'min_order_discount') {
          base.rule_config = JSON.stringify({ min_amount: parseFloat(form.min_amount) || 0 });
        }
        break;
      case 'fixed':
        base.value = parseFloat(form.value) || 0;
        break;
      case 'free_nth_order': {
        const config = { nth_order: parseInt(form.nth_order, 10) || 0 };
        base.rule_config = JSON.stringify(config);
        break;
      }
      case 'buy_x_pay_y': {
        const config = {
          buy_qty: parseInt(form.buy_qty, 10) || 0,
          pay_qty: parseInt(form.pay_qty, 10) || 0,
        };
        if (form.trigger_product_sku) {
          const variant = products.flatMap(p => p.variants || []).find(v => v.sku === form.trigger_product_sku);
          if (variant) config.trigger_variant_id = variant.uuid;
        }
        if (form.reward_product_sku) {
          const variant = products.flatMap(p => p.variants || []).find(v => v.sku === form.reward_product_sku);
          if (variant) config.reward_variant_id = variant.uuid;
        }
        base.rule_config = JSON.stringify(config);
        break;
      }
      case 'free_shipping':
        base.rule_config = '{}';
        break;
      default:
        break;
    }

    return base;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = buildPayload();
      if (offer && !isClone) {
        await apiService.updateOffer(offer.id, payload);
      } else {
        await apiService.addOffer(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const allVariants = products.flatMap(p => (p.variants || []).map(v => ({
    ...v,
    label: `${v.sku} — ${localizedField(p, 'name', 'en') || v.sku}`,
  })));

  const variantOptions = allVariants.map(v => ({ value: v.sku, label: v.label }));
  const categoryOptions = categories.map(c => ({ value: c.name, label: c.name }));

  const showValue = ['percentage', 'fixed', 'min_order_discount'].includes(form.rule_type);
  const showNthOrder = form.rule_type === 'free_nth_order';
  const showMinAmount = form.rule_type === 'min_order_discount';
  const showBuyPay = form.rule_type === 'buy_x_pay_y';

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="add-edit-offer-title">
      <h2 id="add-edit-offer-title">{isClone ? 'Clone Offer' : offer ? 'Edit Offer' : 'Add Offer'}</h2>
      {error ? <div className="modal-error" role="alert">{error}</div> : null}
      <form onSubmit={handleSave} noValidate>
        <div className="form-group">
          <label htmlFor="rule_type">Rule Type *</label>
          <Select
            id="rule_type"
            data={RULE_TYPES}
            value={form.rule_type}
            onChange={(val) => setForm(f => ({ ...f, rule_type: val || 'percentage' }))}
            searchable
          />
          <Text size="xs" c="dimmed" mt={4}>{RULE_HELP[form.rule_type]}</Text>
        </div>

        {showValue && (
          <div className="form-group">
            <label htmlFor="value">{form.rule_type === 'fixed' ? 'Discount Amount (CHF)' : 'Discount Percentage (%)'} *</label>
            <NumberInput
              id="value"
              value={form.value}
              onChange={(val) => handleNumberChange('value', val)}
              min={0}
              max={form.rule_type === 'percentage' || form.rule_type === 'min_order_discount' ? 100 : undefined}
              step={0.01}
              required
              hideControls
            />
          </div>
        )}

        {showMinAmount && (
          <div className="form-group">
            <label htmlFor="min_amount">Minimum Cart Amount (CHF) *</label>
            <NumberInput
              id="min_amount"
              value={form.min_amount}
              onChange={(val) => handleNumberChange('min_amount', val)}
              min={0}
              step={0.10}
              required
              hideControls
            />
          </div>
        )}

        {showNthOrder && (
          <div className="form-group">
            <label htmlFor="nth_order">Free on Nth order *</label>
            <NumberInput
              id="nth_order"
              value={form.nth_order}
              onChange={(val) => handleNumberChange('nth_order', val)}
              min={1}
              step={1}
              required
              hideControls
            />
            <Text size="xs" c="dimmed" mt={4}>E.g. 5 = free product only on the user's 5th order</Text>
          </div>
        )}

        {showBuyPay && (
          <>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="buy_qty">Buy Quantity (paid) *</label>
                <NumberInput
                  id="buy_qty"
                  value={form.buy_qty}
                  onChange={(val) => handleNumberChange('buy_qty', val)}
                  min={2}
                  step={1}
                  required
                  hideControls
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="pay_qty">Get Free Quantity *</label>
                <NumberInput
                  id="pay_qty"
                  value={form.pay_qty}
                  onChange={(val) => handleNumberChange('pay_qty', val)}
                  min={1}
                  step={1}
                  required
                  hideControls
                />
              </div>
            </div>
            <Text size="xs" c="dimmed" mt={-4} mb={8}>Buy {form.buy_qty || '?'} get {form.pay_qty || '?'} free — {parseInt(form.pay_qty, 10) || 0} free per set</Text>
          </>
        )}

        {['percentage', 'fixed', 'buy_x_pay_y'].includes(form.rule_type) && (
          <div className="form-group">
            <label htmlFor="category">Scope — Category (optional)</label>
            <Select
              id="category"
              data={categoryOptions}
              value={form.category || null}
              onChange={(val) => setForm(f => ({ ...f, category: val || '' }))}
              searchable
              clearable
              placeholder="All products"
            />
          </div>
        )}

        {['percentage', 'fixed'].includes(form.rule_type) && (
          <div className="form-group">
            <label htmlFor="product_sku">Product SKU (optional — leave empty for category/all)</label>
            <Select
              id="product_sku"
              data={variantOptions}
              value={form.product_sku || null}
              onChange={(val) => setForm(f => ({ ...f, product_sku: val || '' }))}
              searchable
              clearable
              placeholder="All products in scope"
            />
          </div>
        )}

        {showNthOrder && (
          <div className="form-group">
            <label htmlFor="product_sku">Free Product SKU *</label>
            <Select
              id="product_sku"
              data={variantOptions}
              value={form.product_sku || null}
              onChange={(val) => setForm(f => ({ ...f, product_sku: val || '' }))}
              searchable
              required
              placeholder="Select the product to give free"
            />
          </div>
        )}

        {showBuyPay && (
          <>
            <div className="form-group">
              <label htmlFor="trigger_product_sku">Trigger Product SKU (optional — leave empty for category/all)</label>
              <Select
                id="trigger_product_sku"
                data={variantOptions}
                value={form.trigger_product_sku || null}
                onChange={(val) => setForm(f => ({ ...f, trigger_product_sku: val || '' }))}
                searchable
                clearable
                placeholder="Any product in scope"
              />
            </div>
            <div className="form-group">
              <label htmlFor="reward_product_sku">
                {form.trigger_product_sku ? 'Reward Product SKU (optional — defaults to trigger product)' : 'Reward Product SKU *'}
              </label>
              <Select
                id="reward_product_sku"
                data={variantOptions}
                value={form.reward_product_sku || null}
                onChange={(val) => setForm(f => ({ ...f, reward_product_sku: val || '' }))}
                searchable
                clearable={!!form.trigger_product_sku}
                placeholder={form.trigger_product_sku ? 'Same as trigger product' : 'Select the free reward product'}
              />
            </div>
          </>
        )}

        <LanguageTabs>
          {({ language, langCode }) => (
            <div className="form-group" key={langCode}>
              <label htmlFor={`description_${langCode}`}>Description</label>
              <TextInput
                id={`description_${langCode}`}
                value={localizedField(form, 'description', langCode, null)}
                onChange={(e) => {
                  const existing = (form.translations || []).filter(t => t.language_code !== langCode);
                  setForm(f => ({ ...f, translations: [...existing, { language_code: langCode, description: e.currentTarget.value }] }));
                }}
                placeholder="e.g. Summer Sale — 10% off all Paneer"
              />
            </div>
          )}
        </LanguageTabs>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="start_time">Start Time *</label>
            <input
              id="start_time"
              name="start_time"
              type="datetime-local"
              value={form.start_time}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="end_time">End Time *</label>
            <input
              id="end_time"
              name="end_time"
              type="datetime-local"
              value={form.end_time}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <Checkbox
          name="active"
          label="Active"
          checked={form.active}
          onChange={(e) => setForm(f => ({ ...f, active: e.currentTarget.checked }))}
        />

        <Checkbox
          name="business_only"
          label="For Business Customers Only"
          checked={form.business_only}
          onChange={(e) => setForm(f => ({ ...f, business_only: e.currentTarget.checked }))}
        />

        <div className="btns-container">
          <Button type="submit" loading={loading}>Save</Button>
          <Button variant="subtle" color="gray" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
